import { useForm } from "@tanstack/react-form"
import { useMutation } from "@tanstack/react-query"
import * as z from "zod"

import { X } from "lucide-react"

import { Button } from "@/components/ui/Button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog"
import { Field, FieldError, FieldLabel } from "@/components/ui/Field"
import { Input } from "@/components/ui/Input"
import { Textarea } from "@/components/ui/Textarea"
import { Spinner } from "@/components/ui/Spinner"

interface ContactDialogProps {
  planType: string
  prefilledEmail?: string
  onSubmit: (
    fromAddress: string,
    subject: string,
    body: string,
  ) => Promise<void>
  onSuccess?: () => void
  onError?: () => void
  onClose: () => void
}

function getDefaultBody(planType: string) {
  return `Hi Classkick Team,

I'd like to learn more about the ${planType} plan. Here's some background about my school.

Full Name: 
School: 
District: 
State: 
Phone: 
Department(s): 
Grade Level(s): 
Number of Teacher Licenses: 
What is your familiarity with Classkick: 
When would you like to start with Classkick? 
What else should we know? 

Best regards`
}

function createContactFormSchema() {
  return z.object({
    fromAddress: z
      .string()
      .min(1, "Email is required")
      .pipe(z.email("Please enter a valid email address")),
    subject: z
      .string()
      .min(1, "Subject is required")
      .max(200, "Subject must be at most 200 characters"),
    body: z
      .string()
      .min(1, "Message is required")
      .min(10, "Message must be at least 10 characters")
      .max(5000, "Message must be at most 5000 characters"),
  })
}

function getFieldError(errors: unknown[]): string | undefined {
  if (!errors || errors.length === 0) return undefined
  const error = errors[0]
  if (typeof error === "string") return error
  if (error && typeof error === "object" && "message" in error) {
    return typeof error.message === "string"
      ? error.message
      : "Validation error"
  }
  return "Validation error"
}

export function ContactDialog({
  planType,
  prefilledEmail,
  onSubmit,
  onSuccess,
  onError,
  onClose,
}: ContactDialogProps) {
  const mutation = useMutation({
    mutationFn: async ({
      from,
      subject,
      body,
    }: {
      from: string
      subject: string
      body: string
    }) => {
      const formattedBody = body.replace(/\n/g, "<br>")
      return onSubmit(from, subject, formattedBody)
    },
    onSuccess: () => {
      onClose()
      onSuccess?.()
    },
    onError: () => {
      onClose()
      onError?.()
    },
  })

  const form = useForm({
    defaultValues: {
      fromAddress: prefilledEmail || "",
      subject: `Interested in the ${planType} plan`,
      body: getDefaultBody(planType),
    },
    validators: {
      onBlur: createContactFormSchema(),
      onSubmit: createContactFormSchema(),
    },
    onSubmit: async ({ value }) => {
      mutation.mutate({
        from: value.fromAddress,
        subject: value.subject,
        body: value.body,
      })
    },
  })

  function handleClose() {
    if (mutation.isPending) return
    onClose()
  }

  const toAddress = "partnerships@classkick.com"

  return (
    <Dialog
      open={true}
      onOpenChange={(isOpen: boolean) => !isOpen && handleClose()}
    >
      <DialogContent className="tw-max-w-lg tw-font-sansAlt">
        <DialogHeader>
          <DialogTitle>Contact Our Team</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
        >
          <div className="tw-py-4 tw-space-y-4">
            {/* To Field */}
            <div className="tw-flex tw-items-center tw-gap-2">
              <label className="tw-text-sm tw-font-medium tw-w-12">To:</label>
              <span className="tw-text-sm tw-text-gray-600">{toAddress}</span>
            </div>

            {/* From Field */}
            <form.Field
              name="fromAddress"
              children={(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid
                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel className="tw-text-sm" htmlFor={field.name}>
                      From
                    </FieldLabel>
                    <Input
                      id={field.name}
                      name={field.name}
                      type="email"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      disabled={mutation.isPending}
                      required
                      placeholder="your.email@example.com"
                      aria-invalid={isInvalid}
                      className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-ring-1 focus:tw-border-green-600 focus:tw-ring-green-600 disabled:tw-bg-gray-100 disabled:tw-cursor-not-allowed"
                    />
                    {isInvalid && (
                      <FieldError>
                        <span className="tw-flex tw-text-sm tw-items-center tw-gap-1 tw-pt-2">
                          <X className="tw-w-4 tw-h-4" />
                          {getFieldError(field.state.meta.errors)}
                        </span>
                      </FieldError>
                    )}
                  </Field>
                )
              }}
            />

            {/* Subject Field */}
            <form.Field
              name="subject"
              children={(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid
                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel className="tw-text-sm" htmlFor={field.name}>
                      Subject
                    </FieldLabel>
                    <Input
                      id={field.name}
                      name={field.name}
                      type="text"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      disabled={mutation.isPending}
                      required
                      aria-invalid={isInvalid}
                      className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-ring-1 focus:tw-border-green-600 focus:tw-ring-green-600 disabled:tw-bg-gray-100 disabled:tw-cursor-not-allowed"
                    />
                    {isInvalid && (
                      <FieldError>
                        <span className="tw-flex tw-text-sm tw-items-center tw-gap-1 tw-pt-2">
                          <X className="tw-w-4 tw-h-4" />
                          {getFieldError(field.state.meta.errors)}
                        </span>
                      </FieldError>
                    )}
                  </Field>
                )
              }}
            />

            {/* Body Field */}
            <form.Field
              name="body"
              children={(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid
                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel className="tw-text-sm" htmlFor={field.name}>
                      Message
                    </FieldLabel>
                    <Textarea
                      id={field.name}
                      name={field.name}
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      disabled={mutation.isPending}
                      required
                      rows={10}
                      aria-invalid={isInvalid}
                      className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-ring-1 focus:tw-border-green-600 focus:tw-ring-green-600 disabled:tw-bg-gray-100 disabled:tw-cursor-not-allowed"
                    />
                    {isInvalid && (
                      <FieldError>
                        <span className="tw-flex tw-text-sm tw-items-center tw-gap-1 tw-pt-2">
                          <X className="tw-w-4 tw-h-4" />
                          {getFieldError(field.state.meta.errors)}
                        </span>
                      </FieldError>
                    )}
                  </Field>
                )
              }}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
            >
              {([canSubmit, isSubmitting]) => (
                <Button
                  type="submit"
                  disabled={!canSubmit || mutation.isPending}
                >
                  {mutation.isPending || isSubmitting ? (
                    <>
                      <Spinner className="tw-mr-2 tw-h-4 tw-w-4 tw-text-xl tw-text-white tw-size-8" />
                      Sending...
                    </>
                  ) : (
                    "Send Message"
                  )}
                </Button>
              )}
            </form.Subscribe>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
